import {
  Box,
  TextField,
  CircularProgress,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { useState, useCallback } from 'react';
import { emailAlreadyExists, updateEmployee } from 'service/UserService';
import Buttons from 'components/elements/Buttons';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackBarSuccess, setSnackBarFailed } from 'store/snackBar';
import { setRole } from 'store/auth';
import { type RootState } from 'store/rootReducer';
import { strings } from 'i18n/Strings';
import { debounce } from 'lodash';
import { type EmployeeBody } from 'types';
import { roleIds, roles } from 'constants/constantText';
interface IUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  empId?: string;
}
interface IUpdateUserForm {
  onClose: () => void;
  updateEmployeeData: () => void;
  data: IUser;
}
interface EmailExistsResponse {
  exist: boolean;
}

const UpdateUser = (props: IUpdateUserForm): JSX.Element => {
  const { onClose, updateEmployeeData, data } = props;
  const dispatch = useDispatch();
  const { empId: currentUserEmpId } = useSelector(
    (state: RootState) => state.onboarding,
  );
  const [firstName, setFirstName] = useState(
    data?.firstName ? data?.firstName : '',
  );
  const [lastName, setLastName] = useState(
    data?.lastName ? data?.lastName : '',
  );
  const [email, setEmail] = useState(data?.email ? data?.email : '');
  const [formError, setFormError] = useState<IUser>({});
  const [loading, setLoading] = useState(false);
  const [isApprover, setIsApprover] = useState(
    data?.empHasRoles[0]?.roleId?.toString() === roleIds.CeoRoleId,
  );

  const handleInputChange = (
    type: string,
    e: { target: { value: string } },
  ): undefined | string => {
    switch (type) {
      case 'firstName': {
        setFirstName(e.target.value);
        return;
      }
      case 'lastName': {
        setLastName(e.target.value);
        return;
      }
      case 'email': {
        setEmail(e.target.value);
        return;
      }
      default:
        return '';
    }
  };

  const handleValidation = async (
    type: string,
    e: { target: { value: string } },
  ): Promise<unknown> => {
    const err: IUser = {};
    switch (type) {
      case 'firstName': {
        if (e.target.value === '')
          err.firstName = strings.displayText.firstNameRequiredText;
        setFormError({ ...formError, firstName: err.firstName });
        return;
      }
      case 'lastName': {
        if (e.target.value === '')
          err.lastName = strings.displayText.lastNameRequiredText;
        setFormError({ ...formError, lastName: err.lastName });
        return;
      }
      case 'email': {
        const emailRegex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (e.target.value === '')
          err.email = err.email = strings.displayText.emailEmptyText;
        else if (!emailRegex.test(e.target.value)) {
          err.email = 'Invalid email address';
        } else if (data?.email !== e.target.value) {
          const isExistEmail = await emailAlreadyExists(e.target.value);
          if ((isExistEmail as EmailExistsResponse)?.exist) {
            err.email = strings.displayText.emailExistTest;
          }
        }
        setFormError({ ...formError, email: err.email });
        break;
      }
      default:
        break;
    }
  };

  const debounceHandler = useCallback(
    debounce((type: string, e: { target: { value: string } }) => {
      void handleValidation(type, e);
    }, 600),
    [],
  );

  const handleRoleChange = (selectedRole: boolean): void => {
    setIsApprover(selectedRole);
  };

  const validateForm = (): boolean => {
    const err: IUser = {};
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (email === '') err.email = strings.displayText.emailEmptyText;
    if (!emailRegex.test(email)) {
      err.email = strings.displayText.invalidEmailText;
    }
    if (data?.email !== email) {
      const isExistEmail = emailAlreadyExists(email);
      if ((isExistEmail as unknown as EmailExistsResponse)?.exist) {
        err.email = strings.displayText.emailExistTest;
      }
    }
    if (firstName === '') {
      err.firstName = strings.displayText.firstNameRequiredText;
    }
    if (lastName === '')
      err.lastName = strings.displayText.lastNameRequiredText;

    setFormError({ ...err });
    return Object.keys(err).length < 1;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const formFirstName = formData.get('firstName')?.toString() ?? '';
    const formLastName = formData.get('lastName')?.toString() ?? '';
    const formEmail = formData.get('email')?.toString() ?? '';
    const roleId = isApprover ? roleIds.CeoRoleId : roleIds.MMEmployeeRoleId;
    const role = isApprover ? roles.CeoRole : roles.MMEmployeeRole;
    const value: EmployeeBody =
      formEmail !== data?.email
        ? {
            firstName: formFirstName,
            lastName: formLastName,
            email: formEmail,
            role,
            roleId: parseInt(roleId),
          }
        : {
            firstName: formFirstName,
            lastName: formLastName,
            role,
            roleId: parseInt(roleId),
          };
    const isValid = validateForm();

    if (isValid) {
      void (async () => {
        // create user service here
        setLoading(true);
        updateEmployee(Number(data?.empId), value)
          .then(async () => {
            // Check if we're updating the currently logged-in user's role
            const isUpdatingSelf = Number(data?.empId) === currentUserEmpId;

            if (isUpdatingSelf) {
              // Update Redux store with the new role immediately
              dispatch(setRole(role));
              console.log('Role updated in Redux store to:', role);

              dispatch(
                setSnackBarSuccess({
                  snackBarMessage:
                    strings.displayText.userUpdatedSuccessText +
                    ' Refreshing page...',
                }),
              );
              updateEmployeeData();
              setLoading(false);
              onClose();

              // Reload page immediately to ensure all components reflect the new role
              setTimeout(() => {
                window.location.reload();
              }, 500);
              return;
            }

            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.userUpdatedSuccessText,
              }),
            );
            updateEmployeeData();
            setLoading(false);
            onClose();
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: err.response.data.message,
              }),
            );
            setLoading(false);
            console.log(err);
          });
      })();
    }
  };

  return (
    <Box sx={{ maxWidth: '30rem' }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <TextField
          margin="normal"
          required
          fullWidth
          label="First name"
          value={firstName}
          name="firstName"
          onChange={(e) => {
            handleInputChange('firstName', e);
            debounceHandler('firstName', e);
          }}
          autoFocus
          sx={{
            '& p': {
              color: 'red',
            },
            mb: 2,
          }}
          helperText={formError?.firstName}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label="Last name"
          value={lastName}
          name="lastName"
          onChange={(e) => {
            handleInputChange('lastName', e);
            debounceHandler('lastName', e);
          }}
          sx={{
            '& p': {
              color: 'red',
            },
            mb: 2,
          }}
          helperText={formError?.lastName}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label="Email address"
          value={email}
          name="email"
          autoComplete="email"
          onChange={(e) => {
            handleInputChange('email', e);
            debounceHandler('email', e);
          }}
          sx={{
            '& p': {
              color: 'red',
            },
            mb: 2,
          }}
          helperText={formError?.email}
        />
        <Grid item xs={6} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={isApprover}
                onChange={() => {
                  handleRoleChange(!isApprover);
                }}
              />
            }
            label={strings.displayText.approver}
          />
        </Grid>
        <Buttons
          style={{ py: '0.8rem', mt: '1rem' }}
          type="submit"
          fullWidth={true}
          disabled={loading}
          endIcon={loading ? <CircularProgress size={15} /> : null}
        >
          Save
        </Buttons>
      </Box>
    </Box>
  );
};

export default UpdateUser;
